import { useState, type ReactNode } from "react";
import {
  Check,
  X,
  RotateCcw,
  Pencil,
  Activity,
  Headset,
  AudioLines,
  Rocket,
  ArrowUpRight,
  Instagram,
} from "lucide-react";
import type { DemoFlow, DemoFlowActionSetAction } from "../flowTypes";
import type { DemoFormData } from "../demoTypes";
import { buildExternalTestUrl, normalizePhone } from "../flowBuilder";
import { LIVE_REFERENCE_CONFIG } from "../readyExamples";
import {
  computeMaxScore,
  intentLevelFor,
  intentNarrativeFor,
  buildBehaviorStory,
  type DemoSignalState,
} from "../demoSignal";
import { trackDemoEvent, fetchManagerContact } from "../smartDemoTracking";

type ModalKind = "manager" | "voice" | "install" | null;

const SALES_POINTS = [
  "Kendi görselleriniz",
  "Kendi hizmetleriniz",
  "Kendi müşteri sorularınız",
  "Kendi iletişim kanalınız",
  "Davranış ölçüm adımları",
];

export default function DemoCompletion({
  flow,
  signal,
  data,
  handoffAction,
  onRestart,
  onEdit,
  onEditLabel = "Bilgilerimi Düzenle",
  onCreateCustom,
}: {
  flow: DemoFlow;
  signal: DemoSignalState;
  data: DemoFormData;
  handoffAction: DemoFlowActionSetAction | null;
  onRestart: () => void;
  onEdit: () => void;
  onEditLabel?: string;
  onCreateCustom?: () => void;
}) {
  const [modal, setModal] = useState<ModalKind>(null);

  const maxScore = computeMaxScore(flow);
  const level = intentLevelFor(signal.score);
  const narrative = intentNarrativeFor(signal.score);
  const story = buildBehaviorStory(signal.events);

  const testUrl = handoffAction ? buildExternalTestUrl(handoffAction, data) : null;
  const destination = handoffAction?.destination ?? null;
  const isPhone = destination === "phone";
  const testLabel =
    destination === "whatsapp"
      ? "WhatsApp Yönlendirmesini Test Et"
      : destination === "website"
        ? "Web Sitesini Aç"
        : destination === "instagram"
          ? "Instagram Profilini Aç"
          : destination === "phone"
            ? "Telefon Aramasını Test Et"
            : "Harici Hedefi Aç";
  const testIntro =
    destination === "whatsapp"
      ? "Gerçek WhatsApp yönlendirmesini test etmek ister misiniz?"
      : "Harici hedefi test etmek ister misiniz?";

  return (
    <>
      <div className="mt-5 rounded-2xl border border-background-800 bg-background-900/40 p-5 text-background-100">
        {/* Final headline */}
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
            <Check className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-background-50">
              Demonuzu tamamladınız.
            </h2>
            <p className="text-xs text-background-300">
              Az önce reklamdan gelen bir müşterinin işletmenizde yaşayabileceği örnek yolculuğu
              deneyimlediniz.
            </p>
          </div>
        </div>

        {/* Davranış hikayesi */}
        <div className="mt-5 rounded-xl border border-accent-500/30 bg-accent-500/10 p-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-accent-300" />
            <h3 className="text-sm font-semibold text-background-100">Davranış Özeti</h3>
          </div>

          {story.length > 0 ? (
            <>
              <p className="mt-3 text-sm text-background-200">Bu örnekte müşteri:</p>
              <ul className="mt-2 space-y-1.5">
                {story.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-background-100">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-300" strokeWidth={3} />
                    {s}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm leading-relaxed text-background-100">
                Bu nedenle sistem onu{" "}
                <span className="font-semibold text-accent-300">{narrative}</span> olarak
                sınıflandırdı.
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm text-background-300">Henüz etkileşim kaydedilmedi.</p>
          )}

          <div className="mt-4 flex items-center gap-3 rounded-lg bg-background-950/30 px-3 py-2.5">
            <span className="font-heading text-2xl font-bold text-background-50">
              {signal.score}
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-xs text-background-300">/ {maxScore} Potansiyel Skoru</span>
              <span className="text-xs font-semibold text-accent-300">{level}</span>
            </div>
          </div>
        </div>

        {/* Açıklama — customer-friendly + Meta-safe teknik */}
        <p className="mt-4 text-sm leading-relaxed text-background-200">
          Böylece yalnızca kim mesaj attı değil, hangi ziyaretçilerin daha güçlü satın alma niyeti
          gösterdiği de ölçülebilir.
        </p>
        <p className="mt-2 text-xs leading-relaxed text-background-400">
          Gerçek kurulumda seçilen etkileşimler Meta Pixel / Conversions API gibi ölçüm araçlarına
          uygun event'ler olarak bağlanabilir ve reklam optimizasyonuna veri sağlayabilir. Bu puan,
          demo içindeki davranışlarınızı göstermek için oluşturulan bir simülasyon skorudur.
        </p>

        {/* Final aksiyonlar */}
        <div className="mt-5 space-y-2.5">
          <ActionCard
            icon={<Headset className="h-5 w-5" />}
            title="Yöneticiye Soru Sormak İstiyorum"
            subtitle="Sorunuzu iletin, yöneticiniz sizinle ilgilensin"
            onClick={() => setModal("manager")}
          />
          <ActionCard
            icon={<AudioLines className="h-5 w-5" />}
            title="Yöneticinin Demo Hakkındaki Sesli Mesajını Dinle"
            subtitle="Kısa bir sesli bilgilendirme"
            onClick={() => setModal("voice")}
          />
          <ActionCard
            icon={<Rocket className="h-5 w-5" />}
            title="Sistemi Kurmak İstiyorum"
            subtitle="Gerçek kurulum aşamasına geçin"
            primary
            onClick={() => {
              trackDemoEvent("completion:install-intent", "cta_intent", {
                cta_id: "install",
                intent: "install",
                cta_label: "Sistemi Kurmak İstiyorum",
              });
              setModal("install");
            }}
          />
        </div>

        {/* Satış özeti */}
        <div className="mt-5 rounded-xl border border-background-700 bg-background-800/40 p-4">
          <p className="text-sm font-semibold text-background-100">
            Bu sistem firmanıza kurulduğunda
          </p>
          <ul className="mt-3 space-y-2">
            {SALES_POINTS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm text-background-200">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500/20 text-accent-300">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        {/* İkincil aksiyonlar */}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            onClick={onRestart}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-700 bg-transparent px-4 py-3 text-sm font-medium text-background-200 transition hover:bg-background-800"
          >
            <RotateCcw className="h-4 w-4" />
            Demoyu Yeniden Dene
          </button>
          <button
            onClick={onEdit}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-700 bg-transparent px-4 py-3 text-sm font-medium text-background-200 transition hover:bg-background-800"
          >
            <Pencil className="h-4 w-4" />
            {onEditLabel}
          </button>
        </div>

        {/* Cross-conversion — yalnızca hazır örnek modunda */}
        {onCreateCustom && (
          <div className="mt-4 rounded-xl border border-accent-500/40 bg-accent-500/10 p-4 text-center">
            <p className="text-sm font-medium text-background-100">
              Kendi firmanızla da denemek ister misiniz?
            </p>
            <button
              onClick={onCreateCustom}
              className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-4 py-3 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
            >
              <Rocket className="h-4 w-4" />
              Kendi Firmamla Demo Oluştur
            </button>
          </div>
        )}

        {/* Gerçek WhatsApp testi — yalnızca completion sonrası, düşük öncelikli */}
        {testUrl && (
          <div className="mt-4 rounded-xl border border-background-700 bg-background-800/40 p-4 text-center">
            <p className="text-sm font-medium text-background-200">{testIntro}</p>
            <a
              href={testUrl}
              target={isPhone ? undefined : "_blank"}
              rel={isPhone ? undefined : "nofollow noreferrer"}
              className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-700 bg-transparent px-4 py-3 text-sm font-medium text-background-200 transition hover:bg-background-800"
            >
              <ArrowUpRight className="h-4 w-4" />
              {testLabel}
            </a>
            <p className="mt-2 text-xs text-background-400">
              {isPhone ? "Cihazınızın arama uygulamasında açılır." : "Yeni sekmede açılır."}
            </p>
          </div>
        )}

        {/* Canlı Referans — trust/proof, yalnızca enabled + valid URL mevcutsa */}
        {LIVE_REFERENCE_CONFIG.enabled && LIVE_REFERENCE_CONFIG.instagramUrl && (
          <div className="mt-6 rounded-xl border border-accent-500/30 bg-accent-500/10 p-4 text-center">
            <h3 className="font-heading text-sm font-bold text-background-50">
              Gerçek Bir İşletmede de Test Etmek İster misiniz?
            </h3>
            <p className="mt-1.5 text-xs leading-relaxed text-background-300">
              Az önce deneyimlediğiniz akışın çalışan bir versiyonunu gerçek bir işletmenin
              Instagram hesabında da test edebilirsiniz.
            </p>
            {LIVE_REFERENCE_CONFIG.triggerKeyword && (
              <p className="mt-1.5 text-xs text-background-400">
                Instagram mesaj ekranında {LIVE_REFERENCE_CONFIG.triggerKeyword} yazarak akışı
                başlatabilirsiniz.
              </p>
            )}
            <a
              href={LIVE_REFERENCE_CONFIG.instagramUrl}
              target="_blank"
              rel="nofollow noreferrer"
              className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-4 py-3 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
            >
              <Instagram className="h-4 w-4" />
              Instagram'da Canlı Test Et
            </a>
          </div>
        )}
      </div>

      {modal && (
        <ModalShell onClose={() => setModal(null)}>
          {modal === "manager" && <ManagerContent />}
          {modal === "voice" && <VoiceContent />}
          {modal === "install" && (
            <InstallContent onManager={() => setModal("manager")} onClose={() => setModal(null)} />
          )}
        </ModalShell>
      )}
    </>
  );
}

function ActionCard({
  icon,
  title,
  subtitle,
  primary,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  primary?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition ${
        primary
          ? "border-accent-500 bg-accent-500 text-primary-950 hover:bg-accent-400"
          : "border-background-700 bg-background-800/50 text-background-100 hover:bg-background-800"
      }`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          primary ? "bg-primary-950/15 text-primary-950" : "bg-accent-500/20 text-accent-300"
        }`}
      >
        {icon}
      </span>
      <span className="flex-1 leading-tight">
        <span className={`block text-sm font-semibold ${primary ? "text-primary-950" : "text-background-50"}`}>
          {title}
        </span>
        <span
          className={`mt-0.5 block text-xs ${primary ? "text-primary-900/80" : "text-background-400"}`}
        >
          {subtitle}
        </span>
      </span>
      <ArrowUpRight className={`h-5 w-5 shrink-0 ${primary ? "text-primary-950" : "text-background-400"}`} />
    </button>
  );
}

function ModalShell({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="animate-fade-in-up w-full max-w-md rounded-2xl border border-background-200 bg-background-50 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-foreground-950">Akıllı Demo</h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition hover:bg-background-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ManagerContent() {
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const handleAsk = async () => {
    trackDemoEvent("completion:manager-question", "cta_intent", {
      cta_id: "manager_question",
      intent: "question",
      cta_label: "Yöneticiye Soru Sormak İstiyorum",
    });

    setSubmitting(true);
    setContactError(null);
    const contact = await fetchManagerContact();
    setSubmitting(false);

    if (!contact || !contact.phone) {
      setContactError("Yönetici iletişim bilgisine şu an ulaşılamadı. Lütfen tekrar deneyin.");
      return;
    }

    const phone = normalizePhone(contact.phone);
    const trimmed = question.trim();
    const baseMessage = "Merhaba Metin Bey, Smart Demo üzerinden size bir sorum olacak.";
    const message = trimmed ? `${baseMessage}\n\n${trimmed}` : baseMessage;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col items-center text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
        <Headset className="h-6 w-6" />
      </span>
      <h4 className="mt-3 font-heading text-base font-bold text-foreground-950">
        Yöneticiye Soru Sormak İstiyorum
      </h4>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Sorunuzu yazın, WhatsApp üzerinden yöneticinize iletilir.
      </p>

      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        maxLength={500}
        rows={4}
        placeholder="Sorunuzu yazabilirsiniz"
        className="mt-4 w-full resize-none rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
      />

      {contactError && (
        <p className="mt-3 w-full text-left text-sm text-accent-700">{contactError}</p>
      )}

      <button
        onClick={handleAsk}
        disabled={submitting}
        className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 py-3.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Yönlendiriliyor..." : "WhatsApp'tan Yöneticiye Sor"}
      </button>
    </div>
  );
}

function VoiceContent() {
  const [audioError, setAudioError] = useState(false);

  return (
    <div>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
        <AudioLines className="h-6 w-6" />
      </span>
      <h4 className="mt-3 font-heading text-base font-bold text-foreground-950">
        Yönetici Sesli Mesajı
      </h4>

      {/* Gerçek local MP3 player — autoplay yok, kullanıcı kendi başlatır */}
      <div className="mt-4 rounded-xl border border-background-200 bg-background-100 p-4">
        {audioError ? (
          <p className="text-center text-sm text-foreground-600">Sesli mesaj henüz yüklenmemiş.</p>
        ) : (
          <audio
            controls
            preload="metadata"
            src="/audio/smart-demo/yonetici-demo-sonu.mp3"
            className="w-full"
            onError={() => setAudioError(true)}
          />
        )}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-foreground-600">
        Metin Bey'in deneme sonrası kısa değerlendirmesini dinleyebilirsiniz.
      </p>
    </div>
  );
}

function InstallContent({ onManager, onClose }: { onManager: () => void; onClose: () => void }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
        <Rocket className="h-6 w-6" />
      </span>
      <h4 className="mt-3 font-heading text-base font-bold text-foreground-950">
        Sistemi Kurmak İstiyorum
      </h4>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Harika. Gerçek kurulum aşamasına geçmeye hazırsınız.
      </p>

      <div className="mt-5 flex w-full flex-col gap-2">
        <button
          onClick={onManager}
          className="w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 py-3.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
        >
          Yöneticiyle Görüş
        </button>
        <button
          onClick={onClose}
          className="w-full cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-transparent py-3.5 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
        >
          İşleme Devam Et
        </button>
      </div>
    </div>
  );
}